import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Doctor Slice
 * Redux state management for doctor-related data.
 *
 * @author Civilhealth Development Team
 */

// Types
interface Doctor {
  id: number;
  userId: number;
  username: string;
  email: string;
  mobile: string;
  fullName: string;
  registrationNumber: string;
  qualification: string;
  specialization: string;
  department: string;
  experienceYears: number;
  status: string;
  statusDisplayName: string;
  approvalDate: string;
  shiftStartTime: string;
  shiftEndTime: string;
  availableDays: string;
  maxVisitsPerDay: number;
  averageConsultationTimeMinutes: number;
  rating: number;
  totalReviews: number;
  totalPatientsServed: number;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface DoctorState {
  doctors: Doctor[];
  currentDoctor: Doctor | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: DoctorState = {
  doctors: [],
  currentDoctor: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchDoctors = createAsyncThunk(
  'doctor/fetchDoctors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/doctors');
      // Handle paginated response
      const data = response.data.data;
      if (data.content && Array.isArray(data.content)) {
        return data.content;
      }
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch doctors');
    }
  }
);

export const fetchDoctorById = createAsyncThunk(
  'doctor/fetchDoctorById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/doctors/${id}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch doctor');
    }
  }
);

// Slice
const doctorSlice = createSlice({
  name: 'doctor',
  initialState,
  reducers: {
    clearDoctorError: (state) => {
      state.error = null;
    },
    clearCurrentDoctor: (state) => {
      state.currentDoctor = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDoctors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.loading = false;
        state.doctors = action.payload;
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchDoctorById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDoctor = action.payload;
      })
      .addCase(fetchDoctorById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDoctorError, clearCurrentDoctor } = doctorSlice.actions;
export default doctorSlice.reducer;